import { z } from 'zod';

import { BookingCustomQuestionResponseSchema, BookingCustomQuestionSchema } from './custom-questions';
import { EmailStringSchema } from '../../common';

export enum ParticipantType {
  PARTICIPANT = 'participant',
  PURCHASER = 'purchaser',
}

export const BaseParticipantOrPurchaserSchema = z.object({
  id: z.string().optional(),
  firstName: z.string(),
  lastName: z.string(),
  emailAddress: EmailStringSchema.optional().nullable(),
  phoneNumber: z.string().optional().nullable(),
  type: z.nativeEnum(ParticipantType),
});

export type BaseParticipantOrPurchaser = z.output<typeof BaseParticipantOrPurchaserSchema>;

export const BaseParticipantSchema = BaseParticipantOrPurchaserSchema.omit({ type: true }).extend({
  id: z.string().uuid(),
  priceTierName: z.string(),
  cancelledAt: z.string().datetime().nullable(),
});

/** A guest's pick for one attribute of a resource their price tier consumes (bike Size: Medium). */
export const ParticipantResourceSelectionSchema = z.object({
  resourcePoolId: z.string().uuid(),
  resourceAttributeId: z.string().uuid(),
  resourceAttributeValueId: z.string().uuid(),
});
export type ParticipantResourceSelection = z.infer<typeof ParticipantResourceSelectionSchema>;

export const ParticipantSchema = BaseParticipantOrPurchaserSchema.omit({ type: true }).extend({
  priceTierName: z.string(),
  customQuestionResponses: z.array(BookingCustomQuestionSchema).optional(),
  /** Missing picks mean "any": only the guest-facing checkout requires them. */
  resourceSelections: z.array(ParticipantResourceSelectionSchema).max(20).optional(),
  /** The exact unit the guest chose (map pick). */
  resourcePoolUnitId: z.string().uuid().optional(),
});

export const PurchaserSchema = BaseParticipantOrPurchaserSchema.omit({ type: true })
  .required({ emailAddress: true })
  .extend({ id: z.string().uuid() });

export const PurchaserWithCustomQuestionsSchema = PurchaserSchema.extend({
  customQuestionResponses: z.array(BookingCustomQuestionSchema).optional(),
});

export type Purchaser = z.infer<typeof PurchaserSchema>;

export const ParticipantUpdateSchema = z.object({
  experienceId: z.string().uuid().optional(),
  participant: BaseParticipantOrPurchaserSchema,
  customQuestionResponses: z.array(BookingCustomQuestionResponseSchema).optional(),
});
